import { Grid, MenuItem, Select, Typography } from '@material-ui/core';
import { cloneDeep, isEmpty } from 'lodash-es';
import { MTableToolbar } from 'material-table';
import React, { useEffect, useRef, useState } from 'react';
import { unstable_batchedUpdates } from 'react-dom';

import {
  IIASDashboardTableFilterConfig,
  IIASFirstAndThirdPartyRequestBody,
  queryFirstAndThirdPartyIasData,
} from '~/src/dash/api/ias-wrapper-api/ias-wrapper-api';
import {
  IIASDashboardTableRowData,
  IListTableColumn,
  ListTable,
} from '~/src/dash/components/list-table';
import { publicaColors } from '~/src/dash/utils/colors';
import {
  IIASDashboardQueryParams,
  IIASDashboardSectionTableColumn,
  IIASDashboardSectionTableGroupBy,
  IIASDashboardSectionTableInfo,
} from '~/src/dash/views/ias-publisher/IASDashboard/common/types';
import { IIASDashboardTableBreadCrumb } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardTable/common';
import { IASDashboardTableBreadCrumbs } from '~/src/dash/views/ias-publisher/IASDashboard/IASDashboardTable/IASDashboardTableBreadCrumbs';
import { ISingleSelectOption, SingleSelect } from '~/src/dash/views/singleselect-view';

const generateListTableColumns = (
  groupBy: IIASDashboardSectionTableGroupBy,
  columns: IIASDashboardSectionTableColumn[],
) => {
  const listTableColumns: IListTableColumn[] = [
    {
      field: 'Name',
      title: groupBy.label,
      renderType: [
        'Creatives',
        'Ad Units',
        'Sizes',
        'Traffic Sources',
        'Tiles',
        'Sections',
      ].includes(groupBy.label)
        ? 'normal'
        : 'namelink_returnsnameid',
      searchable: true,
      minWidth: '200px',
      textAlignHeader: 'left',
      paddingLeft: groupBy.label === 'Creatives' ? '6px' : '14px',
    },
  ];
  columns.forEach((col) => {
    listTableColumns.push({
      field: col.dataKey,
      title: col.label,
      renderType: col.renderType,
    });
  });
  return listTableColumns;
};

const extractDataKeysFromColumns = (columns: IIASDashboardSectionTableColumn[]) => {
  const dataKeys: string[] = [];
  columns.forEach((col) => {
    dataKeys.push(col.dataKey);
  });
  return dataKeys;
};

const extractDefaultGroupBy = (groupBys: IIASDashboardSectionTableGroupBy[]) => {
  const result = groupBys.filter((item) => item.isDefault);
  return result.length > 0 ? result[0] : groupBys[0];
};

const filterGroupBy = (
  keyString: string,
  keyValue: string,
  groupBys: IIASDashboardSectionTableGroupBy[],
) => {
  // @ts-ignore
  const result = groupBys.filter((item) => item[keyString] === keyValue);
  return result.length > 0 ? result[0] : groupBys[0];
};

const generateGroupByName = (name: string | undefined, id: string | undefined) => {
  if (name && id) {
    return `${name} (${id})`;
  } else if (name) {
    return `${name}`;
  } else if (id) {
    return `(${id})`;
  } else {
    return `Uncategorized`;
  }
};

const generateListTableData = (
  groupBy: IIASDashboardSectionTableGroupBy,
  columnKeys: string[],
  data: any,
) => {
  const listTableData: IIASDashboardTableRowData[] = [];
  data.forEach((item: Record<string, any>) => {
    const row: Record<string, any> = {
      ID: item.ID,
      Name: generateGroupByName(item[groupBy.dataKey], item.ID),
      [groupBy.dataKey]: item[groupBy.dataKey],
    };
    columnKeys.forEach((key) => {
      row[key] = item[key];
    });
    listTableData.push(row as IIASDashboardTableRowData);
  });
  return listTableData;
};

const generateItemsFromGroupBys = (groupBys: IIASDashboardSectionTableGroupBy[]) => {
  const items: ISingleSelectOption[] = [];
  groupBys.forEach((groupBy) => {
    items.push({
      label: groupBy.label,
      value: groupBy.dataKey,
      subheader: groupBy.subheader,
    });
  });
  return items;
};

interface IIASDashboardTableProps {
  classes: { [key: string]: string };
  tableInfo: IIASDashboardSectionTableInfo;
  queryParams: IIASDashboardQueryParams;
}

const dashboardTableRef = React.createRef<any>();

const SHOW_ALL_TRACKED_ADS = 'Show all tracked ads';
const MIN_TRACKED_ADS_1K = '1000';
const MIN_TRACKED_ADS_10K = '10000';
const MIN_TRACKED_ADS_100K = '100000';
const MIN_TRACKED_ADS_1M = '1000000';

export const IASDashboardTable = ({ classes, tableInfo, queryParams }: IIASDashboardTableProps) => {
  const isMounted = useRef(false);
  const [params, setParams] = useState(queryParams);
  const [info, setInfo] = useState(tableInfo);
  const [data, setData] = useState<any>(null);
  const [currentPageSize, setCurrentPageSize] = useState(10);

  const [isLoading, setIsLoading] = useState(true);
  const [currentGroupBy, setCurrentGroupBy] = useState({} as IIASDashboardSectionTableGroupBy);
  const [filters, setFilters] = useState({} as IIASDashboardTableFilterConfig);

  const [crumbs, setCrumbs] = useState<IIASDashboardTableBreadCrumb[]>([]);
  const [isCrumbUsed, setIsCrumbUsed] = useState(false);

  const [currentFilterTrackedAds, setCurrentFilterTrackedAds] = useState(SHOW_ALL_TRACKED_ADS);

  const { graph, groupBys, columns } = info;

  const { startDate, endDate, timezone, groupingNumber, deviceTypes, teamID, anIDs, publisherID } =
    params;

  // whenever the table info or query params props change, we set them to state and update all the accompanying states
  useEffect(() => {
    if (!isEmpty(tableInfo)) {
      unstable_batchedUpdates(() => {
        setIsLoading(true);
        setParams(queryParams);
        setInfo(tableInfo);
        setCurrentGroupBy({ ...extractDefaultGroupBy(tableInfo.groupBys) });
        setFilters({} as IIASDashboardTableFilterConfig);
        setCrumbs([] as IIASDashboardTableBreadCrumb[]);
      });
    }
  }, [tableInfo, queryParams]);

  // once a new current group by is set, we set isLoading to false in order to run queryData
  useEffect(() => {
    if (isMounted.current) {
      setIsLoading(false);
    }
  }, [currentGroupBy]);

  // once data is set the first time, we set mounted to true
  useEffect(() => {
    if (!isMounted.current) {
      isMounted.current = true;
    }
  }, [data]);

  const handleGroupByChange = (_: string, value: string) => {
    setIsLoading(true);
    const result = groupBys.filter((groupBy) => groupBy.dataKey === value);
    unstable_batchedUpdates(() => {
      setCurrentGroupBy(result[0]);
    });
  };

  const handleFilterTrackedAds = ({ target }: React.ChangeEvent<HTMLInputElement>) => {
    setCurrentFilterTrackedAds(target.value);
    dashboardTableRef.current.onQueryChange();
  };

  const handleBreadCrumbClick = (selectedCrumb: IIASDashboardTableBreadCrumb) => {
    const clonedCrumbs = cloneDeep(crumbs).filter(
      (crumb) => crumb.nodeLevel < selectedCrumb.nodeLevel,
    );
    const result = groupBys.filter((groupBy) => groupBy.label === selectedCrumb.groupByLabel);
    unstable_batchedUpdates(() => {
      setCrumbs(clonedCrumbs);
      setIsLoading(true);
      setFilters(
        clonedCrumbs.length > 0
          ? {
              [clonedCrumbs[clonedCrumbs.length - 1].groupByIDKey]:
                clonedCrumbs[clonedCrumbs.length - 1].id,
            }
          : ({} as IIASDashboardTableFilterConfig),
      );
      setCurrentGroupBy(result[0]);
    });
  };

  const handleBreadCrumbClear = () => {
    unstable_batchedUpdates(() => {
      setCrumbs([] as IIASDashboardTableBreadCrumb[]);
      setIsLoading(true);
      setFilters({});
      setCurrentGroupBy({ ...extractDefaultGroupBy(tableInfo.groupBys) });
    });
  };

  const handleNameLinkClickInfo = (name: string, id: string) => {
    const updatedFilters = {} as IIASDashboardTableFilterConfig;
    updatedFilters[currentGroupBy.idKey] = id;
    if (id && id !== '0') {
      const newCrumb = {
        id,
        label: name,
        groupByLabel: currentGroupBy.label,
        groupByIDKey: currentGroupBy.idKey,
        nodeLevel: currentGroupBy.nodeLevel,
      };
      const clonedCrumbs = cloneDeep(crumbs).filter(
        (crumb) => crumb.nodeLevel < newCrumb.nodeLevel,
      );
      clonedCrumbs.push(newCrumb);
      clonedCrumbs.sort((a, b) => a.nodeLevel - b.nodeLevel);
      unstable_batchedUpdates(() => {
        if (!isCrumbUsed) {
          setIsCrumbUsed(true);
        }
        setCrumbs(clonedCrumbs);
        setIsLoading(filters !== updatedFilters);
        setFilters(updatedFilters);
        setCurrentGroupBy(filterGroupBy('paramKey', currentGroupBy.childKey || '', groupBys));
      });
    }
  };

  const queryData = (
    query: any,
  ): Promise<{ data: IIASDashboardTableRowData[]; page: number; totalCount: number }> => {
    return new Promise((resolve) => {
      const { search, pageSize, orderDirection } = query;
      const nameSearch = search as string;
      const idSearch = /^-?\d+$/.test(search) ? search.toString() : '';
      const currentPage = query.page;
      setCurrentPageSize(pageSize);

      const body1 = {
        GraphID: graph,
        StartDate: startDate,
        EndDate: endDate,
        Timezone: timezone,
        GroupingNumber: groupingNumber,
        DeviceTypes: deviceTypes,
        TeamID: teamID,
        AnIDs: anIDs,
        Filters: filters,
        NameSearch: nameSearch,
        IDSearch: idSearch,
        ReportOrderByKey: columns[0].dataKey,
        ReportOrderBy: orderDirection || 'desc',
        GroupByKey: currentGroupBy.paramKey,
        Page: currentPage,
        PageSize: pageSize,
      } as IIASFirstAndThirdPartyRequestBody;
      queryFirstAndThirdPartyIasData(body1, { publisher_id: publisherID })
        .then((data: any) => {
          const data1 = data[0].rows;
          setData(data1 ? data1 : []);

          const columnKeys = extractDataKeysFromColumns(columns);
          const listTableData = generateListTableData(currentGroupBy, columnKeys, data1);

          const filteredDataByTrackedAds = listTableData.filter(
            (data: IIASDashboardTableRowData) => {
              const trackedImps = parseInt(currentFilterTrackedAds, 10);
              if (parseInt(data.TotalImps, 10) < trackedImps) {
                return false;
              }
              return true;
            },
          );

          resolve({
            data: filteredDataByTrackedAds,
            page: currentPage,
            totalCount: data1[0].TotalRows,
          });
        })
        .catch(() => {
          setData([]);
          resolve({
            data: [],
            page: 0,
            totalCount: 0,
          });
        });
    });
  };

  const renderCustomToolbar = (props: any) => {
    const copyProps = {
      ...props,
      searchFieldStyle: {
        ...props.searchFieldStyle,
        marginLeft: '-48px',
        width: '300px',
        backgroundColor: publicaColors.veryLightGray,
        border: 0,
      },
    };

    return (
      <Grid item className={classes.toolbarRow} style={{ paddingTop: 8, paddingBottom: 16 }}>
        <Grid container direction='row' justify='space-between' alignItems='center'>
          <Grid item container style={{ width: 240, alignItems: 'flex-end' }}>
            <Grid item>
              <img src='/assets/shared/svg/ViewDataBy.svg' />
            </Grid>
            <Grid
              item
              style={{
                paddingLeft: 6,
                paddingRight: 16,
                paddingBottom: 2,
              }}
            >
              <Typography
                style={{
                  fontSize: 14,
                  fontWeight: 500,
                }}
              >
                View Data By
              </Typography>
            </Grid>
            <Grid style={{ display: 'flex', flexDirection: 'row' }}>
              <Grid item>
                <SingleSelect
                  name='Group By'
                  label=''
                  onChange={handleGroupByChange}
                  items={generateItemsFromGroupBys(groupBys)}
                  value={currentGroupBy.dataKey}
                  style={{ fontSize: '13px', minWidth: '180px' }}
                  customMenuHeight={440}
                />
              </Grid>
              <Grid item style={{ marginTop: '16px', marginLeft: '40px' }}>
                <Select
                  name='Filter Tracked ads'
                  value={currentFilterTrackedAds}
                  onChange={handleFilterTrackedAds}
                  style={{ fontSize: 13 }}
                >
                  <MenuItem value={SHOW_ALL_TRACKED_ADS}>Show All Tracked Ads </MenuItem>
                  <MenuItem value={MIN_TRACKED_ADS_1K}>Min Tracked Ads: 1k </MenuItem>
                  <MenuItem value={MIN_TRACKED_ADS_10K}>Min Tracked Ads: 10k </MenuItem>
                  <MenuItem value={MIN_TRACKED_ADS_100K}>Min Tracked Ads: 100k </MenuItem>
                  <MenuItem value={MIN_TRACKED_ADS_1M}>Min Tracked Ads: 1m </MenuItem>
                </Select>
              </Grid>
            </Grid>
          </Grid>
          <Grid item className='MTableToolbarWrapper' style={{ paddingRight: 16 }}>
            <MTableToolbar {...copyProps} />
          </Grid>
        </Grid>
        {!isEmpty(crumbs) ? (
          <Grid item style={{ paddingTop: 8 }}>
            <IASDashboardTableBreadCrumbs
              crumbs={crumbs}
              handleBreadCrumbClick={handleBreadCrumbClick}
              handleBreadCrumbClear={handleBreadCrumbClear}
            />
          </Grid>
        ) : (
          !isCrumbUsed && (
            <Grid
              item
              container
              direction='row'
              style={{
                padding: '8px 24px 8px 16px',
                backgroundColor: publicaColors.veryLightGray,
                width: 'fit-content',
                borderRadius: 4,
              }}
            >
              <Grid style={{ marginTop: 1 }}>
                <img src='/assets/shared/svg/LightBulb.svg' />
              </Grid>
              <Grid style={{ paddingLeft: 8 }}>
                <Typography style={{ fontSize: 14 }}>
                  Drill into any object by clicking on the table below to get a snapshot of all
                  related objects.
                </Typography>
              </Grid>
            </Grid>
          )
        )}
      </Grid>
    );
  };

  return (
    <Grid container direction='column' spacing={2}>
      <Grid item>
        <ListTable
          title=''
          columns={generateListTableColumns(currentGroupBy, columns)}
          data={[]}
          queryData={queryData}
          handleNameLinkClickInfo={handleNameLinkClickInfo}
          enablePagination={true}
          renderCustomToolbar={renderCustomToolbar}
          pageSize={currentPageSize}
          isLoading={isLoading}
          disableSnackbar
          emptyRowsWhenPaging={true}
          disableUpdateFocus={true}
          tableRef={dashboardTableRef}
        />
      </Grid>
    </Grid>
  );
};
